import React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { SafeAreaView, View } from 'react-native';

import { Institution } from '@/api/services/institutions';
import CustomCheckBox from '@/components/SharedComponents/CustomCheckBox/CustomCheckBox';
import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { TextLink } from '@/components/TextLink';
import { Button } from '@/components/ui/therapist/Button';
import Dropdown from '@/components/ui/therapist/Dropdown';
import { Text } from '@/components/ui/therapist/Text';
import colors from '@/config/colors';
import { Links } from '@/config/links';
import { IS_ANDROID } from '@/utils/platform';
import {
  confirmPasswordValidate,
  passwordValidate,
} from '@/utils/passwordUtils';
import { SignUpFormData } from './SignUpFormData';
import { AnonymousStackParamList } from '@/navigations/AnonymousStack';
import { styles } from './styles';

interface TherapistSignUpFormProps {
  control: Control<SignUpFormData>;
  handleSubmit: () => void;
  institutions: Institution[];
  isUseOfDataConfirmed: boolean;
  setUseOfDataConfirmation: (value: boolean) => void;
  isDataStudyConsent: boolean;
  setDataStudyConsent: (value: boolean) => void;
  isNewsletterConfirmed: boolean;
  setNewsletterConfirmation: (value: boolean) => void;
  showNewsletter: boolean;
  isSubmitted: boolean;
  filledPassword: string;
  errors: FieldErrors<SignUpFormData>;
}

export const TherapistSignUpForm: React.FC<TherapistSignUpFormProps> = ({
  control,
  handleSubmit,
  institutions,
  isUseOfDataConfirmed,
  setUseOfDataConfirmation,
  isDataStudyConsent,
  setDataStudyConsent,
  isNewsletterConfirmed,
  setNewsletterConfirmation,
  showNewsletter,
  isSubmitted,
  filledPassword,
  errors,
}) => {
  const { navigate } = useNavigation<NavigationProp<AnonymousStackParamList>>();
  const dataProtectionText = (
    <Text>
      Ich willige ein, dass meine personenbezogenen Daten durch neolexon
      verarbeitet werden, um eine Sprachtherapie durchzuführen. Weitere
      Informationen zu meinen Rechten, der Speicherung und Verarbeitung der
      Daten finde ich in der{' '}
      <TextLink href={Links.datenschutz.href}>Datenschutzerklärung</TextLink>,
      die ich gelesen habe.
      <Text style={{ color: colors.red }}>*</Text>
    </Text>
  );

  const dataProcessingAgreementText = (
    <Text>
      Ich willige ein, dass neolexon meine anonymisierten Nutzungsdaten
      verarbeitet, um die Apps technisch stabil, nutzerfreundlich und
      zukunftsfähig zu gestalten.
    </Text>
  );
  return (
    <SafeAreaView
      style={[
        {
          backgroundColor: colors.newUI.therapist.bgBlue10,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 24,
        },
      ]}
    >
      <KeyboardAwareScrollView enableOnAndroid>
        <Text
          style={[
            styles.title,
            { marginVertical: 24, fontFamily: 'InterSemiBold' },
          ]}
        >
          Kostenlos registrieren und sofort starten
        </Text>
        <Text style={{ marginBottom: 12 }}>
          Mit <Text style={{ color: colors.red }}>*</Text> markierte Felder sind
          erforderlich
        </Text>
        <View
          style={[
            styles.formWrapper,
            {
              backgroundColor: colors.white,
              borderRadius: 12,
              width: '100%',
              maxWidth: 850,
            },
          ]}
        >
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="firstName"
                label="Vorname"
                rules={{ required: 'Vorname wird benötigt.' }}
                autoCapitalize="words"
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="lastName"
                label="Nachname"
                rules={{ required: 'Nachname wird benötigt.' }}
                autoCapitalize="words"
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <Controller
                name="institution"
                control={control}
                rules={{ required: 'Institution wird benötigt.' }}
                render={({ field }) => (
                  <Dropdown
                    statusMessage={errors.institution?.message}
                    onSelectChange={(value: string[]) =>
                      field.onChange(value[0])
                    }
                    status={errors.institution ? 'warning' : undefined}
                    required
                    data={institutions?.map((institution) => ({
                      label: institution.displayName,
                      value: institution.value,
                    }))}
                    label="Institution"
                    value={field.value ? [field.value] : []}
                  />
                )}
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="email"
                label="E-Mail-Adresse"
                keyboardType="email-address"
                rules={{
                  required: 'E-Mail-Adresse wird benötigt.',
                  pattern: {
                    value:
                      /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi,
                    message: 'E-Mail-Adresse ungültig',
                  },
                }}
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="password"
                label="Passwort (mindestens 8 Zeichen)"
                secureTextEntry={true}
                rules={{
                  required: 'Passwort wird benötigt.',
                  minLength: {
                    value: 8,
                    message:
                      'Passwort muss mindestens 8 Zeichen lang sein und darf Buchstaben, Zahlen, Sonderzeichen und Leerzeichen enthalten.',
                  },
                  validate: (value: string) => passwordValidate(value),
                }}
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="confirmPassword"
                label="Wiederholung Passwort"
                secureTextEntry={true}
                rules={{
                  required: 'Passwortwiederholung wird benötigt.',
                  validate: (value: string) =>
                    confirmPasswordValidate(value, filledPassword),
                }}
              />
            </View>
          </View>
          <View style={{ marginHorizontal: 12 }}>
            <View style={styles.checkbox}>
              <CustomCheckBox
                onChecked={() =>
                  setUseOfDataConfirmation(!isUseOfDataConfirmed)
                }
                checked={isUseOfDataConfirmed}
                isLabelComponent={IS_ANDROID}
                label={
                  <PressableNeolexon
                    onPress={() =>
                      setUseOfDataConfirmation(!isUseOfDataConfirmed)
                    }
                  >
                    <Text style={styles.checkBoxText}>
                      {dataProtectionText}{' '}
                      {!isUseOfDataConfirmed && isSubmitted ? (
                        <Text style={styles.mandatoryErrorCheck}>
                          (Pflichtfeld)
                        </Text>
                      ) : null}
                    </Text>
                  </PressableNeolexon>
                }
                disabled={false}
                checkedColor={false}
              />
            </View>
            <View style={styles.checkbox}>
              <CustomCheckBox
                checked={isDataStudyConsent}
                onChecked={() => {
                  setDataStudyConsent(!isDataStudyConsent);
                }}
                isLabelComponent={IS_ANDROID}
                label={
                  <PressableNeolexon
                    onPress={() => setDataStudyConsent(!isDataStudyConsent)}
                  >
                    <Text style={styles.checkBoxText}>
                      {dataProcessingAgreementText}
                    </Text>
                  </PressableNeolexon>
                }
                disabled={false}
                checkedColor={false}
              />
            </View>
            <View style={styles.checkbox}>
              {showNewsletter ? (
                <CustomCheckBox
                  checked={isNewsletterConfirmed}
                  onChecked={() =>
                    setNewsletterConfirmation(!isNewsletterConfirmed)
                  }
                  isLabelComponent={IS_ANDROID}
                  label={
                    <View>
                      <PressableNeolexon
                        onPress={() =>
                          setNewsletterConfirmation(!isNewsletterConfirmed)
                        }
                      >
                        <Text style={styles.checkBoxText}>
                          Ich möchte den Newsletter erhalten.
                        </Text>
                      </PressableNeolexon>
                    </View>
                  }
                  disabled={false}
                  checkedColor={false}
                />
              ) : null}
            </View>
            <Text style={{ margin: 12 }}>
              Ich kann meine Einwilligungen jederzeit widerrufen. Mit meiner
              Registrierung stimme ich den 
              <TextLink href={Links.agb.href}>AGB</TextLink> zu.
            </Text>
          </View>
        </View>
        <View
          style={{
            marginTop: 24,
            justifyContent: 'space-between',
            flexDirection: 'row',
          }}
        >
          <Button
            label="Zum Login"
            onPress={() => navigate('Login', {})}
            look="tertiary"
          />
          <Button
            label="Registrieren"
            onPress={() => handleSubmit()}
            look="primary"
          />
        </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  );
};
